import React from 'react';
import { ActivityResponse, LeaderboardPeriod } from '../../services/gamificationApi';

export const T = {
    primary:      '#005581',
    primaryLight: '#4F99BD',
    primaryGrad:  'linear-gradient(135deg, #4F99BD 0%, #005581 100%)',
    bg:           '#f1f5f9',
    card:         '#ffffff',
    border:       '#e2e8f0',
    text:         '#0f172a',
    muted:        '#475569',
    faint:        '#64748b',
    radius:       '14px',
    font:         "'Source Sans 3', system-ui, -apple-system, sans-serif",
    shadow:       '0 1px 4px rgba(0,0,0,0.06)',
};

export const LEVEL_COLORS: Record<string, { bg: string; text: string }> = {
    Novice:            { bg: '#f1f5f9', text: '#475569' },
    Practitioner:      { bg: '#dbeafe', text: '#1e40af' },
    Proficient:        { bg: '#dcfce7', text: '#166534' },
    Expert:            { bg: '#fef3c7', text: '#92400e' },
    'Master Educator': { bg: '#fee2e2', text: '#991b1b' },
};

export const PERIOD_OPTIONS: Array<{ value: LeaderboardPeriod; label: string }> = [
    { value: 'week',     label: 'This Week' },
    { value: 'month',    label: 'This Month' },
    { value: 'all_time', label: 'All Time' },
];

export const MEDALS = ['🥇', '🥈', '🥉'];

export function initials(name: string) {
    return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export function relativeTime(iso: string | null): string {
    if (!iso) return '—';
    const then = new Date(iso);
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const diffDays = Math.floor((now.setHours(0, 0, 0, 0) - new Date(then).setHours(0, 0, 0, 0)) / dayMs);
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
}

// Local calendar date as `YYYY-MM-DD`, built field by field.
//
// `toISOString().slice(0, 10)` looks like the same thing but converts to UTC
// first, so a local midnight in a positive-offset zone (e.g. +05:30) reports the
// previous day.
export function localDateKey(d: Date): string {
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
}

// Sessions since the start of the current calendar week (Monday 00:00 local).
// A rolling trailing-7-days window would never reset, so a user who finished a
// week over goal would still see that count on Monday morning. "Weekly Goal"
// implies a fresh start, so the counter has to zero out on the week boundary.
//
// `ActivityDay.date` is a bare calendar date, not a timestamp, so the week
// boundary is compared as a string. `new Date("2026-07-27")` parses date-only
// ISO as UTC midnight, which lands on the previous day in every negative-offset
// zone (including the product default, America/Chicago) and silently drops
// Monday's sessions from the week.
//
// Shared with MyProgressPage, which owns the goal itself: the two surfaces have
// to agree on what "this week" means.
export function sessionsThisCalendarWeek(activity: ActivityResponse | null): number {
    if (!activity) return 0;
    const weekStart = new Date();
    // getDay(): 0=Sun … 6=Sat. Shift back to Monday; Sunday counts as day 7 of the prior week.
    weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
    const startKey = localDateKey(weekStart);
    return activity.days.reduce((sum, d) => (d.date >= startKey ? sum + d.count : sum), 0);
}

// A small uppercase caption above a filter control.
export function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
            <span style={{ fontSize: '0.66rem', fontWeight: 700, color: T.faint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
            {children}
        </div>
    );
}

export const filterInputStyle: React.CSSProperties = {
    fontFamily: T.font, fontSize: '0.8rem', padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${T.border}`, color: T.text, background: '#fff',
    width: '100%', boxSizing: 'border-box',
};

export function RangeInputs({ min, max, onMinChange, onMaxChange }: {
    min: string; max: string; onMinChange: (v: string) => void; onMaxChange: (v: string) => void;
}) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" min={0} value={min} onChange={e => onMinChange(e.target.value)} placeholder="Min" style={{ ...filterInputStyle, flex: 1, minWidth: 0 }} />
            <span style={{ color: T.faint, fontSize: '0.78rem', flexShrink: 0 }}>–</span>
            <input type="number" min={0} value={max} onChange={e => onMaxChange(e.target.value)} placeholder="Max" style={{ ...filterInputStyle, flex: 1, minWidth: 0 }} />
        </div>
    );
}

